import { AdminAffiliatesResponse, AffiliateAccountStatus, AffiliateAccountWithUser } from '@avanzu/types';
import {
  Box,
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../../utils/formatDate';

type StatusFilter = 'ALL' | AffiliateAccountStatus;

export const statusChipColor = (
  status: AffiliateAccountStatus
): 'warning' | 'success' | 'error' => {
  switch (status) {
    case AffiliateAccountStatus.APPROVED:
      return 'success';
    case AffiliateAccountStatus.SUSPENDED:
      return 'error';
    default:
      return 'warning';
  }
};

export default function AffiliatesList() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState<AffiliateAccountWithUser[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/affiliates');

      if (!response.ok) {
        throw new Error('Failed to fetch affiliates');
      }

      const data: AdminAffiliatesResponse = await response.json();
      setAffiliates(data.affiliates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching affiliates:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered =
    statusFilter === 'ALL' ? affiliates : affiliates.filter((a) => a.status === statusFilter);

  return (
    <PaperSection title="Affiliates">
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Tabs
        value={statusFilter}
        onChange={(_, value) => setStatusFilter(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="All" value="ALL" />
        <Tab label="Pending" value={AffiliateAccountStatus.PENDING} />
        <Tab label="Approved" value={AffiliateAccountStatus.APPROVED} />
        <Tab label="Suspended" value={AffiliateAccountStatus.SUSPENDED} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Referral Code</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Registered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No affiliates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((affiliate) => (
                  <TableRow
                    key={affiliate.id}
                    onClick={() => navigate(`/app/affiliates/${affiliate.id}`)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <TableCell>
                      {`${affiliate.user.firstName} ${affiliate.user.lastName}`.trim() || '-'}
                    </TableCell>
                    <TableCell>{affiliate.businessName || '-'}</TableCell>
                    <TableCell>{affiliate.user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={affiliate.status}
                        color={statusChipColor(affiliate.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {affiliate.referralCode}
                    </TableCell>
                    <TableCell>{(affiliate.commissionRateBps / 100).toFixed(0)}%</TableCell>
                    <TableCell>{affiliate.clientCount}</TableCell>
                    <TableCell>
                      {formatDate(affiliate.createdAt, DateFormatOptions.DATEONLY)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && filtered.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {filtered.length} affiliate{filtered.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </PaperSection>
  );
}
